/**
 * Which section of a long document is being read.
 *
 * ── WHY THIS IS A FUNCTION AND NOT FOUR LINES INSIDE THE EFFECT ───────────
 * There is a known-wrong implementation of this and a known-right one, and
 * the wrong one LOOKS correct in a screenshot of the top of the page.
 *
 * WRONG: an IntersectionObserver watching a thin band pinned under the nav,
 * marking the FIRST section that intersects it. That band is where the
 * OUTGOING section's tail sits, so the rail runs one section behind, and it
 * only ever resolves at all for sections taller than the viewport. Measured
 * on the live P3 terms page before the fix: 1 of 21 headings marked
 * correctly, and nothing marked at the top of the page.
 *
 * RIGHT, and what this is: a position read. The current section is the LAST
 * one whose top has passed a reading line just below the nav. That is true
 * wherever a section starts and however tall it is.
 *
 * Pulling it out of the component makes both halves of that distinction
 * testable without a DOM, which is the only way the wrong version stays gone.
 * See memory `project-terms-policies-page` and tests/contents-rail.test.ts.
 */

export interface SectionTop {
  readonly id: string;
  /** The section's top edge in VIEWPORT coordinates, as a rect reports it. */
  readonly top: number;
}

/**
 * @param sections in document order, tops in viewport coordinates
 * @param readingLine px below the viewport top; must exceed the sections'
 *        own `scroll-margin-top`, or a section jumped to by a rail click
 *        lands just above the line and the rail marks the one before it
 * @param atBottom true when the viewport is resting at the end of the page
 */
export function currentSectionId(
  sections: readonly SectionTop[],
  readingLine: number,
  atBottom: boolean,
): string {
  if (sections.length === 0) return "";
  const first = sections[0];
  const last = sections[sections.length - 1];
  /* The final sections are shorter than the run of page left below them, so
     they can never reach the line. At the bottom, the last one is what is
     being read, whatever the arithmetic above says. */
  if (atBottom && last) return last.id;
  /* Before the first heading reaches the line the reader is in the first
     section, not in none of them. */
  let current = first ? first.id : "";
  for (const section of sections) {
    if (section.top <= readingLine) current = section.id;
  }
  return current;
}
